import { toast } from "react-toastify";
import axios from 'axios';

//export const TOKEN_EXPIRY_TIME = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
// Only refresh token API calls needed - no expiry time needed

// Session expire function removed - only API calls needed

export const refreshToken = async (refreshTokenValue) => {
  try {
    const API_URL = process.env.REACT_APP_API_URL;
    // const tenantId = localStorage.getItem("tenantId");
    
    console.log('Calling refresh token API:', `${API_URL}auth/refresh-token`);
    
    const response = await axios.post(`${API_URL}auth/refresh-token?refreshToken=${refreshTokenValue}`, {
      headers: {
        'Content-Type': 'application/json',
        // tenantId: tenantId,
      }
    });

    console.log('Refresh token API response:', response.data?.data?.token);
    if (response.data && response.data.status==200) {
      // Update the stored token
      localStorage.setItem('auth_token', response.data.data.token);
      localStorage.setItem('refresh_token', response.data.data.refreshToken);
      
      
      
      console.log('Token refreshed successfully');
      return response.data.token;
    }
    
    throw new Error('No token received from refresh endpoint');
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
};

export const handleSessionExpired = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  stopRefreshTimer();
  stopUserDetailsTimer();
  toast.error('Session expired. Please login again.');
  window.location.href = '/login';
};

// Test function to manually trigger refresh (for debugging)
export const testRefreshToken = async () => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (refreshTokenValue) {
    console.log('Testing manual token refresh...');
    try {
      await refreshToken(refreshTokenValue);
      console.log('Manual refresh successful');
    } catch (error) {
      console.error('Manual refresh failed:', error);
    }
  } else {
    console.log('No refresh token found for testing');
  }
};

// Global variables to persist timers across component unmounts
let globalRefreshTimer = null;
let globalUserDetailsTimer = null;
let isTimersSetup = false;
let refreshInFlightPromise = null;
let visibilityRefreshHandler = null;
let lastHiddenAt = 0;
let lastEventRefreshAt = 0;
let responseInterceptorId = null;

const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const VISIBILITY_MIN_IDLE_MS = 60 * 1000; // 1 minute
const VISIBILITY_COOLDOWN_MS = 30 * 1000; // 30 seconds

const runRefreshCycle = async (trigger = "interval", { force = false } = {}) => {
  const now = Date.now();
  if (!force && trigger !== "interval" && now - lastEventRefreshAt < VISIBILITY_COOLDOWN_MS) {
    console.log(`Skipping ${trigger} refresh due to cooldown`);
    return false;
  }

  const refreshTokenValue = localStorage.getItem('refresh_token');
  console.log(`Refresh trigger: ${trigger}. Refresh token exists:`, !!refreshTokenValue);

  if (!refreshTokenValue) {
    console.log('No refresh token found in localStorage');
    return false;
  }

  if (refreshInFlightPromise) {
    console.log('Refresh already in progress, waiting for existing request...');
    try {
      await refreshInFlightPromise;
      return true;
    } catch (error) {
      return false;
    }
  }

  refreshInFlightPromise = (async () => {
    try {
      await refreshToken(refreshTokenValue);
      lastEventRefreshAt = Date.now();
      return true;
    } catch (error) {
      console.error('Refresh token API failed:', error);
      return false;
    } finally {
      refreshInFlightPromise = null;
    }
  })();

  return refreshInFlightPromise;
};

const setupAxios401Interceptor = () => {
  if (responseInterceptorId !== null) return;

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      if (!originalRequest || status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      const requestUrl = originalRequest.url || "";
      if (requestUrl.includes("auth/refresh-token")) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const refreshed = await runRefreshCycle("401-retry", { force: true });
      if (!refreshed) {
        return Promise.reject(error);
      }

      const updatedToken = localStorage.getItem("auth_token");
      if (updatedToken) {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${updatedToken}`,
        };
      }

      return axios(originalRequest);
    }
  );
};

export const setupTokenRefresh = () => {
  // Only setup timer once
  if (isTimersSetup) {
    console.log('Refresh timer already setup, skipping...');
    return;
  }

  // Clear any existing timer first (safety measure)
  if (globalRefreshTimer) {
    clearInterval(globalRefreshTimer);
    globalRefreshTimer = null;
  }

  console.log('Setting up refresh token API calls every 30 minutes...');

  // Refresh immediately so first request doesn't fail with stale token.
  runRefreshCycle("initial-setup", { force: true });

  // Call refresh token API every 30 minutes
  globalRefreshTimer = setInterval(async () => {
    await runRefreshCycle("interval");
  }, REFRESH_INTERVAL_MS);

  // Refresh once when user returns after tab is hidden (no click-based refresh).
  if (!visibilityRefreshHandler && typeof document !== "undefined") {
    visibilityRefreshHandler = () => {
      if (document.visibilityState === "hidden") {
        lastHiddenAt = Date.now();
        return;
      }

      if (document.visibilityState === "visible") {
        const hiddenDuration = Date.now() - lastHiddenAt;
        if (hiddenDuration >= VISIBILITY_MIN_IDLE_MS) {
          runRefreshCycle("visibility-return");
        }
      }
    };

    document.addEventListener("visibilitychange", visibilityRefreshHandler);
  }

  setupAxios401Interceptor();

  isTimersSetup = true;
  console.log('Refresh token API timer set up successfully');
};

// Function to start refresh token API calls (call this in your main App component or layout)
export const startTokenRefresh = () => {
  setupTokenRefresh();
};

// Function to fetch user details API
export const fetchUserDetails = async () => {
  try {
    const API_URL = process.env.REACT_APP_API_URL;
    const authToken = localStorage.getItem('auth_token');
    const userEmail = localStorage.getItem('email');
    
    if (!userEmail) {
      console.log('No user email found in localStorage');
      return;
    }
    
    console.log('Calling fetch user details API:', `${API_URL}auth/fetch-user-details?email=${userEmail}`);
    
    const response = await axios.get(`${API_URL}auth/fetch-user-details?email=${userEmail}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      }
    });

    console.log('Fetch user details API response:', response.data);
    
    // Check if user is active
    if (response.data && response.data.length > 0) {
      const userData = response.data[0];
      if (userData.active === false) {
        console.log('User is inactive, logging out...');
        handleUserLogout();
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Fetch user details API failed:', error);
    throw error;
  }
};

// Function to handle user logout when inactive
const handleUserLogout = () => {
  // Clear all localStorage data
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('role');
  localStorage.removeItem('userId');
  localStorage.removeItem('user');
  localStorage.removeItem('tenantId');
  localStorage.removeItem('email');
  localStorage.removeItem('clinicId');
  localStorage.removeItem('clinilogo');
  localStorage.removeItem('clinicname');
  localStorage.removeItem('mobile');
  localStorage.removeItem('firstName');
  localStorage.removeItem('lastName');
  
  // Clear cookies
  document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
  
  // Stop all timers
  stopRefreshTimer();
  stopUserDetailsTimer();
  
  // Show logout message
  toast.error('Your account has been deactivated. Please contact the admin.');
  
  // Redirect to login page
  window.location.href = '/login';
};

// Function to setup user details API calls every 30 minutes
export const setupUserDetailsFetch = () => {
  // Clear any existing timer first (safety measure)
  if (globalUserDetailsTimer) {
    clearInterval(globalUserDetailsTimer);
    globalUserDetailsTimer = null;
  }

  console.log('Setting up fetch user details API calls every 30 minutes...');
  
  // Call fetch user details API every 30 minutes
  globalUserDetailsTimer = setInterval(async () => {
    console.log('User details timer triggered');
    
    try {
      console.log('Calling fetch user details API...');
      await fetchUserDetails();
      console.log('User details fetched successfully');
    } catch (error) {
      console.error('Fetch user details API failed:', error);
      console.log('Continuing despite API failure...');
    }
  }, 30 * 60 * 1000); // 30 minutes

  console.log('Fetch user details API timer set up successfully');
};

// Function to start user details API calls
export const startUserDetailsFetch = () => {
  setupUserDetailsFetch();
};

// Function to stop user details timer
export const stopUserDetailsTimer = () => {
  console.log('Stopping user details timer...');
  if (globalUserDetailsTimer) {
    clearInterval(globalUserDetailsTimer);
    globalUserDetailsTimer = null;
  }
};

// Function to stop refresh timer (call this on logout)
export const stopRefreshTimer = () => {
  console.log('Stopping refresh timer...');
  if (globalRefreshTimer) {
    clearInterval(globalRefreshTimer);
    globalRefreshTimer = null;
  }

  if (visibilityRefreshHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityRefreshHandler);
    visibilityRefreshHandler = null;
  }

  isTimersSetup = false;
};